import React, { useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { v1Post, v1Patch } from '@/lib/api';
import {
  validateMasterCode,
  validateMasterDisplayName,
  validateMasterCodeUnique,
  validateMasterNameUnique,
  masterDuplicateConflictField,
} from '@/lib/masterDataValidation';
import { useIdSelection } from '@/hooks/useIdSelection';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusPill } from '@/components/StatusPill';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const errDetail = (e, fallback) => {
  const detail = e?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};

const PARTNER_DEFAULTS = {
  partner_visible: false,
  partner_description: '',
  partner_seniorities: ['jr', 'mid', 'sr', 'lead'],
  partner_supported_types: ['A', 'C'],
  partner_pipeline_id: '',
};
const BLANK_ROLE_FORM = { code: '', name: '', status: 'active', department_id: '', ...PARTNER_DEFAULTS };
const BLANK_ROLE_FIELD_ERRORS = { code: '', name: '' };

function isRoleFormDirty(form, baseline = BLANK_ROLE_FORM) {
  if (!!form.code.trim() || !!form.name.trim() || form.status !== baseline.status) return true;
  if ((form.department_id || '') !== (baseline.department_id || '')) return true;
  if (!!form.partner_visible !== !!baseline.partner_visible) return true;
  if (form.partner_visible) {
    if ((form.partner_description || '') !== (baseline.partner_description || '')) return true;
    const sen = [...(form.partner_seniorities || [])].sort().join(',');
    const types = [...(form.partner_supported_types || [])].sort().join(',');
    const defSen = [...(baseline.partner_seniorities || [])].sort().join(',');
    const defTypes = [...(baseline.partner_supported_types || [])].sort().join(',');
    if (sen !== defSen || types !== defTypes) return true;
  }
  return false;
}

function partnerPayloadFromForm(form) {
  return {
    partner_visible: !!form.partner_visible,
    partner_description: form.partner_visible ? (form.partner_description || null) : null,
    partner_seniorities: form.partner_visible ? form.partner_seniorities : null,
    partner_supported_types: form.partner_visible ? form.partner_supported_types : null,
  };
}

function validatePartnerForm(form) {
  if (!form.partner_visible) return null;
  if (!form.partner_seniorities?.length) return 'Select at least one seniority.';
  if (!form.partner_supported_types?.length) return 'Select at least one evaluation type.';
  return null;
}

const SENIORITIES = ['jr', 'mid', 'sr', 'lead'];
const PARTNER_TYPES = ['A', 'C'];

function PartnerRoleFields({ form, setForm, toggleListValue, pipelines, idPrefix }) {
  const boundPipeline = (pipelines || []).find((p) => p.id === form.partner_pipeline_id);
  return (
    <>
      <div className="flex items-center gap-2 rounded-md border p-3">
        <Checkbox
          id={`${idPrefix}-partner-visible`}
          checked={form.partner_visible}
          onCheckedChange={(v) => setForm({ ...form, partner_visible: !!v })}
          data-testid={`${idPrefix}-role-partner-visible`}
        />
        <label htmlFor={`${idPrefix}-partner-visible`} className="text-sm">
          Partner catalog (Candidate Portal)
        </label>
      </div>
      {form.partner_visible && (
        <>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Partner description</label>
            <Input
              value={form.partner_description}
              onChange={(e) => setForm({ ...form, partner_description: e.target.value })}
              maxLength={200}
              data-testid={`${idPrefix}-role-partner-description`}
            />
          </div>
          {form.partner_pipeline_id ? (
            <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
              Dedicated pipeline:{' '}
              <span className="font-medium text-foreground">
                {boundPipeline?.label || boundPipeline?.pipeline_key || form.partner_pipeline_id}
              </span>
              {' '}(auto-bound 1:1; configure stages in Pipelines)
            </div>
          ) : (
            <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
              A dedicated draft pipeline is created automatically with this role.
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-muted-foreground">Seniorities</label>
            <div className="mt-1 flex flex-wrap gap-3">
              {SENIORITIES.map((s) => (
                <label key={s} className="flex items-center gap-1.5 text-sm">
                  <Checkbox
                    checked={form.partner_seniorities.includes(s)}
                    onCheckedChange={() => toggleListValue('partner_seniorities', s)}
                  />
                  {s}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Supported types</label>
            <div className="mt-1 flex flex-wrap gap-3">
              {PARTNER_TYPES.map((t) => (
                <label key={t} className="flex items-center gap-1.5 text-sm">
                  <Checkbox
                    checked={form.partner_supported_types.includes(t)}
                    onCheckedChange={() => toggleListValue('partner_supported_types', t)}
                  />
                  Type {t}
                </label>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}

export function RolesSection({
  roles,
  allRoles,
  pipelines = [],
  departments = [],
  defaultDepartmentId = '',
  contextLabel = '',
  isLoading,
  isError,
  onRequestDelete,
  onRegisterClearSelection,
}) {
  const qc = useQueryClient();
  const roleSel = useIdSelection();
  const uniquenessRoles = allRoles || roles;

  React.useEffect(() => {
    onRegisterClearSelection?.(roleSel.clear);
  }, [onRegisterClearSelection, roleSel.clear]);

  const [roleOpen, setRoleOpen] = useState(false);
  const [roleForm, setRoleForm] = useState({ ...BLANK_ROLE_FORM });
  const [roleFormBaseline, setRoleFormBaseline] = useState({ ...BLANK_ROLE_FORM });
  const [roleFieldErrors, setRoleFieldErrors] = useState({ ...BLANK_ROLE_FIELD_ERRORS });
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const discardConfirmedRef = useRef(false);
  const [editRole, setEditRole] = useState(null);
  const [editRoleForm, setEditRoleForm] = useState({
    name: '',
    status: 'active',
    ...PARTNER_DEFAULTS,
  });
  const [editRoleNameError, setEditRoleNameError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const roleIds = useMemo(() => roles.map((r) => r.id), [roles]);
  const allRolesSelected = roleIds.length > 0 && roleIds.every((id) => roleSel.selected.has(id));
  const pipelineLabelById = useMemo(() => {
    const m = new Map();
    for (const p of pipelines) {
      m.set(p.id, p.label || p.pipeline_key || p.id);
    }
    return m;
  }, [pipelines]);

  const blankWithDefaultDept = () => ({
    ...BLANK_ROLE_FORM,
    department_id: defaultDepartmentId || '',
  });

  const resetRoleForm = () => {
    const baseline = blankWithDefaultDept();
    setRoleForm(baseline);
    setRoleFormBaseline(baseline);
    setRoleFieldErrors({ ...BLANK_ROLE_FIELD_ERRORS });
  };

  const openAddRole = () => {
    const baseline = blankWithDefaultDept();
    setRoleForm(baseline);
    setRoleFormBaseline(baseline);
    setRoleFieldErrors({ ...BLANK_ROLE_FIELD_ERRORS });
    setRoleOpen(true);
  };

  const applyMasterConflict = (e, fallback, setFieldErrors) => {
    const raw = e?.response?.data?.detail;
    const field = masterDuplicateConflictField(raw);
    if (field) {
      setFieldErrors((prev) => ({ ...prev, [field]: raw }));
      return;
    }
    toast.error(errDetail(e, fallback));
  };

  const handleRoleDialogOpenChange = (open) => {
    if (!open) {
      if (isRoleFormDirty(roleForm, roleFormBaseline)) {
        setRoleOpen(false);
        setDiscardConfirmOpen(true);
        return;
      }
      resetRoleForm();
    }
    setRoleOpen(open);
  };

  const handleDiscardConfirmOpenChange = (open) => {
    setDiscardConfirmOpen(open);
    if (!open) {
      if (discardConfirmedRef.current) {
        discardConfirmedRef.current = false;
        return;
      }
      if (isRoleFormDirty(roleForm, roleFormBaseline)) {
        setRoleOpen(true);
      }
    }
  };

  const confirmDiscardRoleForm = () => {
    discardConfirmedRef.current = true;
    resetRoleForm();
    setRoleOpen(false);
    setDiscardConfirmOpen(false);
  };

  const createRole = async () => {
    setRoleFieldErrors({ ...BLANK_ROLE_FIELD_ERRORS });
    const codeCheck = validateMasterCode(roleForm.code);
    if (!codeCheck.ok) return toast.error(codeCheck.message);
    const codeUnique = validateMasterCodeUnique(codeCheck.value, uniquenessRoles, { entityLabel: 'Role' });
    if (!codeUnique.ok) return setRoleFieldErrors({ code: codeUnique.message, name: '' });
    const nameCheck = validateMasterDisplayName(roleForm.name);
    if (!nameCheck.ok) return toast.error(nameCheck.message);
    const nameUnique = validateMasterNameUnique(nameCheck.value, uniquenessRoles, { entityLabel: 'Role' });
    if (!nameUnique.ok) return setRoleFieldErrors({ code: '', name: nameUnique.message });
    if (!roleForm.department_id) return toast.error('Department is required.');
    const partnerErr = validatePartnerForm(roleForm);
    if (partnerErr) return toast.error(partnerErr);
    const payload = {
      code: codeCheck.value,
      name: nameCheck.value,
      status: roleForm.status,
      department_id: roleForm.department_id,
      ...partnerPayloadFromForm(roleForm),
    };
    setSubmitting(true);
    try {
      await v1Post('/master/roles', payload);
      qc.invalidateQueries({ queryKey: ['v1', 'roles'] });
      qc.invalidateQueries({ queryKey: ['v1-master-roles'] });
      qc.invalidateQueries({ queryKey: ['v1-pipelines'] });
      toast.success(`Role ${payload.name} created.`);
      setRoleOpen(false);
      resetRoleForm();
    } catch (e) {
      applyMasterConflict(e, 'Could not create role.', setRoleFieldErrors);
    } finally {
      setSubmitting(false);
    }
  };

  const openEditRole = (r) => {
    setEditRole(r);
    setEditRoleForm({
      name: r.name,
      status: r.status,
      department_id: r.department_id || '',
      partner_visible: !!r.partner_visible,
      partner_description: r.partner_description || '',
      partner_seniorities: r.partner_seniorities?.length ? [...r.partner_seniorities] : ['jr', 'mid', 'sr', 'lead'],
      partner_supported_types: r.partner_supported_types?.length ? [...r.partner_supported_types] : ['A', 'C'],
      partner_pipeline_id: r.partner_pipeline_id || '',
    });
    setEditRoleNameError('');
  };

  const toggleCreateListValue = (key, value) => {
    setRoleForm((prev) => {
      const cur = new Set(prev[key] || []);
      if (cur.has(value)) cur.delete(value);
      else cur.add(value);
      return { ...prev, [key]: Array.from(cur) };
    });
  };

  const toggleEditListValue = (key, value) => {
    setEditRoleForm((prev) => {
      const cur = new Set(prev[key] || []);
      if (cur.has(value)) cur.delete(value);
      else cur.add(value);
      return { ...prev, [key]: Array.from(cur) };
    });
  };

  const saveRole = async () => {
    if (!editRole) return;
    setEditRoleNameError('');
    const nameCheck = validateMasterDisplayName(editRoleForm.name);
    if (!nameCheck.ok) return toast.error(nameCheck.message);
    const nameUnique = validateMasterNameUnique(nameCheck.value, uniquenessRoles, {
      excludeId: editRole.id,
      entityLabel: 'Role',
    });
    if (!nameUnique.ok) return setEditRoleNameError(nameUnique.message);
    if (!editRoleForm.department_id) return toast.error('Department is required.');
    const partnerErr = validatePartnerForm(editRoleForm);
    if (partnerErr) return toast.error(partnerErr);
    const payload = {
      name: nameCheck.value,
      status: editRoleForm.status,
      department_id: editRoleForm.department_id,
      ...partnerPayloadFromForm(editRoleForm),
    };
    setSubmitting(true);
    try {
      await v1Patch(`/master/roles/${editRole.id}`, payload);
      qc.invalidateQueries({ queryKey: ['v1', 'roles'] });
      qc.invalidateQueries({ queryKey: ['v1-master-roles'] });
      qc.invalidateQueries({ queryKey: ['v1-pipelines'] });
      toast.success(`Role ${payload.name} updated.`);
      setEditRole(null);
    } catch (e) {
      const raw = e?.response?.data?.detail;
      if (masterDuplicateConflictField(raw) === 'name') setEditRoleNameError(raw);
      else toast.error(errDetail(e, 'Could not update role.'));
    } finally {
      setSubmitting(false);
    }
  };

  const askDeleteRole = (r) => {
    onRequestDelete({
      kind: 'role',
      ids: [r.id],
      label: `Delete role “${r.name}”? It will be removed from master lists but kept in the database.`,
    });
  };

  const askBulkDeleteRoles = () => {
    const ids = roleIds.filter((id) => roleSel.selected.has(id));
    if (ids.length === 0) return;
    onRequestDelete({
      kind: 'role',
      ids,
      label: ids.length === 1
        ? 'Delete 1 selected role? It will be removed from master lists but kept in the database.'
        : `Delete ${ids.length} selected roles? They will be removed from master lists but kept in the database.`,
    });
  };

  return (
    <>
      <Card className="card-elevated flex h-full min-h-0 flex-col overflow-hidden" data-testid="roles-section">
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b p-4">
          <div>
            <p className="text-sm font-semibold">
              {contextLabel ? `Roles in ${contextLabel}` : 'Roles'}
            </p>
            <p className="text-xs text-muted-foreground">
              {isLoading ? 'Loading…' : `${roles.length} role${roles.length === 1 ? '' : 's'}`}
              {roleSel.count > 0 ? ` · ${roleSel.count} selected` : ''}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {roleSel.count > 0 && (
              <Button
                size="sm"
                variant="ghost"
                className="h-8 gap-1.5 text-rose-700 hover:bg-rose-50"
                onClick={askBulkDeleteRoles}
                data-testid="roles-delete-selected"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete All Selected ({roleSel.count})
              </Button>
            )}
            <Button size="sm" variant="outline" className="gap-1.5" onClick={openAddRole} data-testid="add-role-button">
              <Plus className="h-4 w-4" /> Add role
            </Button>
          </div>
        </div>
        {isError ? (
          <p className="p-4 text-sm text-destructive" data-testid="roles-error">Could not load roles.</p>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow className="sticky top-0 z-10 bg-secondary/40">
                <TableHead className="w-10">
                  <Checkbox
                    checked={allRolesSelected}
                    onCheckedChange={(v) => roleSel.setAll(roleIds, v === true)}
                    disabled={roleIds.length === 0}
                    aria-label="Select all roles"
                    data-testid="roles-select-all"
                  />
                </TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Partner</TableHead>
                <TableHead>Pipeline</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {roles.length === 0 && !isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">
                    {contextLabel
                      ? `No roles in ${contextLabel} yet. Add a role to get started.`
                      : 'No roles yet. Add a role to get started.'}
                  </TableCell>
                </TableRow>
              ) : (
                roles.map((r) => (
                  <TableRow key={r.id} data-testid="role-row">
                    <TableCell>
                      <Checkbox
                        checked={roleSel.selected.has(r.id)}
                        onCheckedChange={(v) => roleSel.toggle(r.id, v === true)}
                        aria-label={`Select role ${r.name}`}
                        data-testid={`role-select-${r.code}`}
                      />
                    </TableCell>
                    <TableCell><code className="rounded bg-secondary px-1.5 py-0.5 text-xs">{r.code}</code></TableCell>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell><StatusPill status={r.status} size="sm" /></TableCell>
                    <TableCell>
                      {r.partner_visible ? (
                        <span className="text-xs font-medium text-emerald-700" data-testid="role-partner-yes">Portal</span>
                      ) : (
                        <span className="text-xs text-muted-foreground" data-testid="role-partner-no">—</span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[10rem] truncate text-xs text-muted-foreground" title={r.partner_pipeline_id || ''}>
                      {r.partner_pipeline_id
                        ? (pipelineLabelById.get(r.partner_pipeline_id) || r.partner_pipeline_id.slice(0, 8))
                        : '—'}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-0.5">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => openEditRole(r)}
                          aria-label={`Edit role ${r.name}`}
                          data-testid="edit-role-button"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-rose-700 hover:bg-rose-50"
                          onClick={() => askDeleteRole(r)}
                          aria-label={`Delete role ${r.name}`}
                          data-testid="delete-role-button"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>
        )}
      </Card>

      <Dialog open={roleOpen} onOpenChange={handleRoleDialogOpenChange}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="add-role-dialog">
          <DialogHeader>
            <DialogTitle>Add new role</DialogTitle>
            <DialogDescription>Roles are used across candidates, pipelines, and question-bank tagging.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Role code</label>
              <Input
                value={roleForm.code}
                onChange={(e) => {
                  setRoleForm({ ...roleForm, code: e.target.value.toUpperCase() });
                  if (roleFieldErrors.code) setRoleFieldErrors({ ...roleFieldErrors, code: '' });
                }}
                placeholder="e.g. EM"
                aria-invalid={!!roleFieldErrors.code}
                data-testid="role-code-input"
              />
              {roleFieldErrors.code ? (
                <p className="mt-1 text-xs text-destructive" data-testid="role-code-error">{roleFieldErrors.code}</p>
              ) : (
                <p className="mt-1 text-[11px] text-muted-foreground">Short identifier shown in tables and pipeline keys.</p>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Display name</label>
              <Input
                value={roleForm.name}
                onChange={(e) => {
                  setRoleForm({ ...roleForm, name: e.target.value });
                  if (roleFieldErrors.name) setRoleFieldErrors({ ...roleFieldErrors, name: '' });
                }}
                placeholder="e.g. Engineering Manager"
                aria-invalid={!!roleFieldErrors.name}
                data-testid="role-name-input"
              />
              {roleFieldErrors.name ? (
                <p className="mt-1 text-xs text-destructive" data-testid="role-name-error">{roleFieldErrors.name}</p>
              ) : null}
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Department</label>
              <Select
                value={roleForm.department_id || undefined}
                onValueChange={(v) => setRoleForm({ ...roleForm, department_id: v })}
              >
                <SelectTrigger data-testid="role-department-select"><SelectValue placeholder="Select department" /></SelectTrigger>
                <SelectContent>
                  {(departments || []).map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name}{d.industry_name ? ` · ${d.industry_name}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Status</label>
              <Select value={roleForm.status} onValueChange={(v) => setRoleForm({ ...roleForm, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <PartnerRoleFields
              form={roleForm}
              setForm={setRoleForm}
              toggleListValue={toggleCreateListValue}
              pipelines={pipelines}
              idPrefix="add"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => handleRoleDialogOpenChange(false)} disabled={submitting}>Cancel</Button>
            <Button onClick={createRole} disabled={submitting} data-testid="role-create-confirm">{submitting ? 'Creating…' : 'Create role'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={discardConfirmOpen} onOpenChange={handleDiscardConfirmOpenChange}>
        <AlertDialogContent data-testid="add-role-discard-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>You have unsaved changes. Are you sure you want to discard them?</AlertDialogTitle>
            <AlertDialogDescription>
              Your entered role details will be lost if you discard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="add-role-continue-editing">Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDiscardRoleForm} data-testid="add-role-discard-confirm">
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!editRole} onOpenChange={(v) => !v && setEditRole(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="edit-role-dialog">
          <DialogHeader>
            <DialogTitle>Edit role</DialogTitle>
            <DialogDescription>Update the display name or status. The role code is immutable once created.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Role code</label>
              <Input value={editRole?.code || ''} disabled data-testid="edit-role-code-input" />
              <p className="mt-1 text-[11px] text-muted-foreground">The code is locked because it is referenced by candidates, pipelines, and question banks.</p>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Department</label>
              <Select
                value={editRoleForm.department_id || undefined}
                onValueChange={(v) => setEditRoleForm({ ...editRoleForm, department_id: v })}
              >
                <SelectTrigger data-testid="edit-role-department-select"><SelectValue placeholder="Select department" /></SelectTrigger>
                <SelectContent>
                  {(departments || []).map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name}{d.industry_name ? ` · ${d.industry_name}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Display name</label>
              <Input
                value={editRoleForm.name}
                onChange={(e) => {
                  setEditRoleForm({ ...editRoleForm, name: e.target.value });
                  if (editRoleNameError) setEditRoleNameError('');
                }}
                placeholder="e.g. Engineering Manager"
                aria-invalid={!!editRoleNameError}
                data-testid="edit-role-name-input"
              />
              {editRoleNameError ? (
                <p className="mt-1 text-xs text-destructive" data-testid="edit-role-name-error">{editRoleNameError}</p>
              ) : null}
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Status</label>
              <Select value={editRoleForm.status} onValueChange={(v) => setEditRoleForm({ ...editRoleForm, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <PartnerRoleFields
              form={editRoleForm}
              setForm={setEditRoleForm}
              toggleListValue={toggleEditListValue}
              pipelines={pipelines}
              idPrefix="edit"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditRole(null)} disabled={submitting}>Cancel</Button>
            <Button onClick={saveRole} disabled={submitting} data-testid="role-edit-confirm">{submitting ? 'Saving…' : 'Save changes'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
